// UI-1 (UX review 20 Jun 2026): collaborator lists were rendering raw principal
// IDs — bare Cognito UUIDs (e.g. "895e4448…") and namespaced group/org IDs
// (e.g. "group:agg-team…") — whenever a grant had no resolved email. This helper
// turns any grantee into a human-readable label, preferring an email, then a
// backend-resolved display name, then a friendly form of the namespaced/UUID id.

export interface GranteeLike {
  grantee_email?: string | null
  grantee_display_name?: string | null
  grantee_id: string
}

const GROUP_PREFIX = 'group:'
const EXTORG_PREFIX = 'extorg:'
// A bare principal id that looks like a Cognito sub / UUID (no '@', long, hex-ish).
const UUID_LIKE = /^[0-9a-f]{6,}-?[0-9a-f-]*$/i

/** Human-readable label for a collaborator/grantee. Never returns a raw UUID. */
export function granteeLabel(g: GranteeLike): string {
  const email = (g.grantee_email || '').trim()
  if (email && email.includes('@')) return email

  const display = (g.grantee_display_name || '').trim()
  if (display) return display

  const id = (g.grantee_id || '').trim()
  if (id.startsWith(GROUP_PREFIX)) return `Group: ${id.slice(GROUP_PREFIX.length)}`
  if (id.startsWith(EXTORG_PREFIX)) return `Organisation: ${id.slice(EXTORG_PREFIX.length)}`
  // Bare UUID/sub with no resolvable name — show a short, less alarming form
  // rather than a 36-char identifier.
  if (!id.includes('@') && id.length >= 12 && UUID_LIKE.test(id)) return `User ${id.slice(0, 8)}`
  return id || 'Unknown'
}

/** First-letter avatar initial for a grantee (always a letter/char, never blank). */
export function granteeInitial(g: GranteeLike): string {
  return (granteeLabel(g).charAt(0) || '?').toUpperCase()
}
